import React, { useState, useEffect } from 'react';
import { apiService, GalleryItem } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';
import { Plus, Edit, Trash2, Image as ImageIcon } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';

const Gallery: React.FC = () => {
  const { showToast } = useToast();
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<GalleryItem | null>(null);
  const [filter, setFilter] = useState('all');

  const galleryTypes = [
    { value: 'project', label: 'Projects' },
    { value: 'service', label: 'Services' },
    { value: 'team', label: 'Team' },
    { value: 'office', label: 'Office' },
  ];

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const data = await apiService.getGallery();
      setItems(data || []);
    } catch (error) {
      console.error('Failed to fetch gallery items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);

    // Normalize boolean
    if (fd.has('is_active')) {
      const val = fd.get('is_active');
      fd.set('is_active', val === 'on' || val === 'true' ? 'true' : 'false');
    } else {
      fd.append('is_active', 'false');
    }

    try {
      if (editingItem) {
        await apiService.updateGalleryItem(editingItem.id, fd);
      } else {
        await apiService.createGalleryItem(fd);
      }

      setShowForm(false);
      setEditingItem(null);
      showToast('Gallery image saved successfully!', 'success');
      fetchItems();
    } catch (error) {
      console.error('Failed to save gallery item:', error);
      showToast('Error saving image.', 'error');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Delete this image from gallery?')) {
      try {
        await apiService.deleteGalleryItem(id);
        showToast('Image deleted successfully!', 'success');
        fetchItems();
      } catch (error) {
        console.error('Failed to delete gallery item:', error);
        showToast('Failed to delete image.', 'error');
      }
    }
  };

  const filteredItems = filter === 'all' ? items : items.filter(item => item.gallery_type === filter);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gallery</h1>
          <p className="text-gray-600">Manage images for projects, team, and services</p>
        </div>
        <button
          onClick={() => { setEditingItem(null); setShowForm(true); }}
          className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Image
        </button>
      </div>

      <div className="mb-8 flex items-center gap-2 overflow-x-auto pb-2">
        <button
          onClick={() => setFilter('all')}
          className={`px - 4 py - 1.5 rounded - full text - xs font - bold transition ${filter === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'} `}
        >
          All
        </button>
        {galleryTypes.map(type => (
          <button
            key={type.value}
            onClick={() => setFilter(type.value)}
            className={`px - 4 py - 1.5 rounded - full text - xs font - bold whitespace - nowrap transition ${filter === type.value ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'} `}
          >
            {type.label}
          </button>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <h3 className="text-xl font-bold text-gray-900">
                {editingItem ? 'Edit Image' : 'Add Image'}
              </h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input name="title" defaultValue={editingItem?.title} required className="w-full border border-gray-200 rounded-lg px-4 py-2" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select name="gallery_type" defaultValue={editingItem?.gallery_type || 'project'} className="w-full border border-gray-200 rounded-lg px-4 py-2">
                    {galleryTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
                  <input type="number" name="order" defaultValue={editingItem?.order || 0} className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Alt Text</label>
                <input name="image_alt" defaultValue={editingItem?.image_alt} className="w-full border border-gray-200 rounded-lg px-4 py-2" placeholder="Describe image for SEO" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea name="description" defaultValue={editingItem?.description} rows={2} className="w-full border border-gray-200 rounded-lg px-4 py-2" />
              </div>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Image File</label>
                  <input type="file" name="image" accept="image/*" required={!editingItem} className="block w-full text-xs text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-gray-100 file:text-gray-700" />
                  {editingItem?.image && (
                    <div className="mt-2 h-20 w-32 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                      <img src={getImageUrl(editingItem.image) || undefined} alt="" className="h-full w-full object-cover" />
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 pt-5">
                  <input type="checkbox" name="is_active" defaultChecked={editingItem?.is_active !== false} className="h-5 w-5 text-orange-600 rounded" />
                  <span className="text-sm font-bold text-gray-700">Active</span>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t border-gray-50">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-gray-600 font-medium">Cancel</button>
                <button type="submit" className="px-8 py-2 bg-orange-600 text-white rounded-lg font-bold hover:bg-orange-700 transition">
                  {editingItem ? 'Update' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredItems.map((item) => (
          <div key={item.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden group">
            <div className="relative h-48">
              <img src={getImageUrl(item.image) || undefined} alt={item.title} className="w-full h-full object-cover" />
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                <button onClick={() => { setEditingItem(item); setShowForm(true); }} className="p-2 bg-white rounded shadow text-orange-600 hover:bg-orange-50"><Edit className="h-4 w-4" /></button>
                <button onClick={() => handleDelete(item.id)} className="p-2 bg-white rounded shadow text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="p-4">
              <h3 className="font-bold text-sm text-gray-900 truncate">{item.title}</h3>
              <div className="flex items-center justify-between mt-2">
                <span className="text-[10px] font-bold text-gray-400 uppercase">{item.gallery_type}</span>
                {!item.is_active && <span className="text-[10px] text-red-400 font-bold uppercase">Hidden</span>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredItems.length === 0 && (
        <div className="text-center py-24 bg-gray-50 rounded-xl border border-gray-100">
          <ImageIcon className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No images found.</p>
        </div>
      )}
    </div>
  );
};

export default Gallery;